import mixpanel from "mixpanel-browser";
import { RouteRecordRaw, createRouter, createWebHistory } from "vue-router";

import { flushAll } from "@/composables/pending-flush";
import type * as Layouts from "@/layouts";
import ComplianceEvidenceDetailPage from "@/live/views/ComplianceEvidenceDetailPage.vue";
import ComplianceEvidencePage from "@/live/views/ComplianceEvidencePage.vue";
import LiveDomainsPage from "@/live/views/DomainsPage.vue";
import LivePage from "@/live/views/Page.vue";
import LiveProcessesPage from "@/live/views/ProcessesPage.vue";
import LiveProductsPage from "@/live/views/ProductsPage.vue";
import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import ActivityMetrics from "@/views/ActivityMetrics.vue";
import Application from "@/views/Application.vue";
import AutoLogin from "@/views/AutoLogin.vue";
import Biz from "@/views/Biz.vue";
import BusinessEntityPage from "@/views/BusinessEntityPage.vue";
import CodePreviewDemo from "@/views/CodePreviewDemo.vue";
import CreateStek from "@/views/CreateStek.vue";
import Dev from "@/views/Dev.vue";
import DocsHome from "@/views/DocsHome.vue";
import DocumentPage from "@/views/DocumentPage.vue";
import Domain from "@/views/Domain.vue";
import DomainPage from "@/views/DomainPage.vue";
import DomainTermPage from "@/views/DomainTermPage.vue";
import ForgotPassword from "@/views/ForgotPassword.vue";
import Help from "@/views/Help.vue";
import Home from "@/views/Home.vue";
import IntegrationManagement from "@/views/IntegrationManagement.vue";
import LiveModeEventsDocs from "@/views/LiveModeEventsDocs.vue";
import Login from "@/views/Login.vue";
import NotFoundComponent from "@/views/NotFound.vue";
import OAuthAuthorize from "@/views/OAuthAuthorize.vue";
import OAuthCallback from "@/views/OAuthCallback.vue";
import Ops from "@/views/Ops.vue";
import OrgInst from "@/views/OrgInst.vue";
import OrganizationHome from "@/views/OrganizationHome.vue";
import PrivacyPolicy from "@/views/PrivacyPolicy.vue";
import Process from "@/views/Process.vue";
import ProcessDefinitionPage from "@/views/ProcessDefinitionPage.vue";
import ProcessElementPage from "@/views/ProcessElementPage.vue";
import ProcessPage from "@/views/ProcessPage.vue";
import Product from "@/views/Product.vue";
import ProductPage from "@/views/ProductPage.vue";
import Profile from "@/views/Profile.vue";
import RedirectByIdPage from "@/views/RedirectByIdPage.vue";
import RedirectByNamePage from "@/views/RedirectByNamePage.vue";
import RolePage from "@/views/RolePage.vue";
import Service from "@/views/Service.vue";
import SignUp from "@/views/SignUp.vue";
import SpinnerDemo from "@/views/SpinnerDemo.vue";
import StekManagement from "@/views/StekManagement.vue";
import StekPage from "@/views/StekPage.vue";
import StekSettings from "@/views/StekSettings.vue";
import TaskEvaluation from "@/views/TaskEvaluation.vue";
import TermsOfUse from "@/views/TermsOfUse.vue";
import UserManagement from "@/views/UserManagement.vue";
import WhatsNewManagement from "@/views/WhatsNewManagement.vue";
import StrategyVisionPage from "@/views/strategy/StrategyVisionPage.vue";

declare module "vue-router" {
    interface RouteMeta {
        requiresAuth?: boolean;
        requiresOrg?: boolean;
        requiresStek?: boolean;
        breadcrumbs?: string[] | "auto";
        layout?: keyof typeof Layouts;
        mode?: "design" | "live";
        hideSidebar?: boolean;
        preventRedirect?: boolean;
    }
}

const baseRoutes: Array<RouteRecordRaw> = [
    {
        path: "/home",
        name: "home",
        component: Home,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/login",
        name: "login",
        component: Login,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/oauth/authorize",
        name: "oauth-authorize",
        component: OAuthAuthorize,
        meta: {
            layout: "LoggedOutPageLayout",
            requiresAuth: true,
        },
    },
    {
        path: "/signup",
        name: "signup",
        component: SignUp,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/privacy-policy",
        name: "privacyPolicy",
        component: PrivacyPolicy,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/terms-of-use",
        name: "termsOfUse",
        component: TermsOfUse,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/forgot-password",
        name: "forgotPassword",
        component: ForgotPassword,
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/biz",
        name: "biz",
        component: Biz,
    },
    {
        path: "/ops",
        name: "ops",
        component: Ops,
    },
    {
        path: "/domain",
        name: "domain",
        component: Domain,
    },
    {
        path: "/product",
        name: "product",
        component: Product,
    },
    {
        path: "/process",
        name: "process",
        component: Process,
    },
    {
        path: "/application",
        name: "application",
        component: Application,
    },
    {
        path: "/service",
        name: "service",
        component: Service,
    },
    {
        path: "/docs",
        name: "docsHome",
        component: DocsHome,
        meta: {
            layout: "ApplicationLayout",
            hideSidebar: true,
        },
    },
    {
        path: "/help",
        name: "help",
        component: Help,
        meta: {
            hideSidebar: true,
        },
    },
    {
        path: "/docs/core/live-mode-events",
        name: "liveModeEventsDocs",
        component: LiveModeEventsDocs,
        meta: {
            layout: "ApplicationLayout",
            hideSidebar: true,
        },
    },
    {
        path: "/docs/live-mode-events",
        redirect: "/docs/core/live-mode-events",
    },
    {
        path: "/settings",
        component: Profile,
        meta: {
            requiresAuth: true,
            hideSidebar: true,
        },
        children: [
            {
                path: "",
                name: "settings",
                redirect: { name: "settings-general" },
            },
            {
                path: "general",
                name: "settings-general",
                component: Profile,
                meta: {
                    requiresAuth: true,
                    hideSidebar: true,
                },
            },
            {
                path: "organizations",
                name: "settings-organizations",
                component: Profile,
                meta: {
                    requiresAuth: true,
                    hideSidebar: true,
                },
            },
            {
                path: "security",
                name: "settings-security",
                component: Profile,
                meta: {
                    requiresAuth: true,
                    hideSidebar: true,
                },
            },
            {
                path: "api-keys",
                name: "settings-api-keys",
                component: Profile,
                meta: {
                    requiresAuth: true,
                    hideSidebar: true,
                },
            },
        ],
    },
    {
        path: "/oauth/callback",
        name: "oauthCallback",
        component: OAuthCallback,
        meta: {
            requiresStek: false,
            requiresAuth: true,
            requiresOrg: false,
        },
    },
    {
        path: "/profile",
        redirect: "/settings",
    },
    {
        path: "/profile/org/:orgId",
        redirect: (to) => `/settings/org/${to.params.orgId}`,
    },
    {
        path: "/profile/:orgId/:stekId/integrations",
        redirect: (to) =>
            `/settings/${to.params.orgId}/${to.params.stekId}/integrations`,
    },
    {
        path: "/activity-metrics",
        name: "activityMetrics",
        component: ActivityMetrics,
        meta: {
            requiresAuth: true,
            requiresOrg: false, // Platform-wide
            requiresStek: false,
            hideSidebar: true,
            layout: "ApplicationLayout",
        },
    },
    {
        path: "/:orgName?",
        name: "organizationPage",
        component: OrgInst,
        meta: {
            requiresAuth: true,
            requiresOrg: true,
            requiresStek: true,
            layout: "ApplicationLayout",
        },
        children: [
            {
                path: "",
                name: "organizationHome",
                component: OrganizationHome,
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                    hideSidebar: true,
                },
            },
            {
                path: "stek/create",
                name: "createStek",
                component: CreateStek,
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                    hideSidebar: true,
                },
            },
            {
                path: "manage/steks",
                name: "stekManagement",
                component: StekManagement,
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                    hideSidebar: true,
                },
            },
            {
                path: "manage/whats-new",
                name: "whatsNewManagement",
                component: WhatsNewManagement,
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                    hideSidebar: true,
                },
            },
            {
                path: "user",
                name: "userManagement",
                component: UserManagement,
                meta: {
                    requiresStek: false,
                    requiresAuth: true,
                    requiresOrg: true,
                    hideSidebar: true,
                },
            },
            {
                path: "steks",
                component: undefined,
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: true,
                },
                children: [
                    {
                        path: ":stekName",
                        component: undefined,
                        children: [
                            {
                                path: "integrations",
                                name: "integrationManagement",
                                component: IntegrationManagement,
                                meta: {
                                    hideSidebar: true,
                                },
                            },
                            {
                                path: "settings",
                                name: "stekSettings",
                                component: StekSettings,
                                meta: {
                                    hideSidebar: true,
                                },
                            },
                            {
                                path: "",
                                alias: "biz/domain",
                                name: "stekPage",
                                component: StekPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "strategy",
                                redirect: { name: "strategyVision" },
                            },
                            {
                                path: "strategy/vision",
                                name: "strategyVision",
                                component: StrategyVisionPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live",
                                name: "livePage",
                                component: LivePage,
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/domains",
                                name: "liveDomainsPage",
                                component: LiveDomainsPage,
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/domain/:domainId",
                                name: "liveDomainPage",
                                component: LivePage,
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/products",
                                name: "liveProductsPage",
                                component: LiveProductsPage,
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/product/:productId",
                                name: "liveProductPage",
                                component: LivePage,
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/processes",
                                name: "liveProcessesPage",
                                component: LiveProcessesPage,
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/process/:processId",
                                name: "liveProcessPage",
                                component: LivePage,
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/compliance-evidence",
                                name: "complianceEvidencePage",
                                component: ComplianceEvidencePage,
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/compliance-evidence-detail",
                                name: "complianceEvidenceDetailPage",
                                component: ComplianceEvidenceDetailPage,
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "domain/:domainId",
                                name: "domainPage",
                                component: DomainPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "domain/:domainId/document/:artefactId?",
                                name: "domainDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: "product/:productId",
                                name: "productPage",
                                component: ProductPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "role/:roleId",
                                name: "rolePage",
                                component: RolePage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "business_entity/:businessEntityId",
                                name: "businessEntityPage",
                                component: BusinessEntityPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "domain_term/:domainTermId",
                                name: "domainTermPage",
                                component: DomainTermPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "product/:productId/document/:artefactId?",
                                name: "productDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: "process/:processId",
                                name: "processPage",
                                component: ProcessPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/process-definition/definition/:version?",
                                name: "processDefinitionPage",
                                component: ProcessDefinitionPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/document/:artefactId?",
                                name: "processDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: "process/:processId/task/:taskId",
                                name: "taskPage",
                                component: RedirectByIdPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/task/:taskId/document/:artefactId?",
                                name: "taskDocumentPage",
                                component: RedirectByIdPage,
                            },
                            {
                                path: ":elementType/:elementId",
                                name: "processElementPage",
                                component: ProcessElementPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "document/:artefactId/:version?",
                                name: "documentPage",
                                component: DocumentPage,
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "biz/domain/:domainName",
                                name: "domainDashboard",
                                component: RedirectByNamePage,
                                children: [
                                    {
                                        path: "",
                                        name: "domainOverview",
                                        component: RedirectByNamePage,
                                    },
                                    {
                                        path: "roles",
                                        name: "roles",
                                        component: RedirectByNamePage,
                                    },
                                    {
                                        path: "entities",
                                        name: "entities",
                                        component: RedirectByNamePage,
                                    },
                                    {
                                        path: "files/:filePath?",
                                        name: "domainFiles",
                                        component: RedirectByNamePage,
                                    },
                                ],
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/:filePath?",
                                name: "productInst",
                                component: RedirectByNamePage,
                                props: true,
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/process/:processName/:filePath?",
                                name: "processInst",
                                component: RedirectByNamePage,
                                props: true,
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/process/:processName/tasks/:taskName/:filePath?",
                                name: "taskInst",
                                component: RedirectByNamePage,
                                props: true,
                            },
                            {
                                name: "not-found",
                                path: "not-found",
                                component: NotFoundComponent,
                            },
                            {
                                path: ":pathMatch(.*)*",
                                component: NotFoundComponent,
                            },
                        ],
                    },
                    {
                        path: ":pathMatch(.*)*",
                        component: NotFoundComponent,
                    },
                ],
            },
        ],
    },
    {
        path: "/:pathMatch(.*)*",
        component: NotFoundComponent,
    },
];

const devRoute: RouteRecordRaw = {
    path: "/dev",
    name: "dev",
    component: Dev,
    meta: {
        requiresAuth: false,
        layout: "DevLayout",
    },
    children: [
        {
            path: "autologin",
            name: "autologin",
            component: AutoLogin,
            meta: {
                requiresAuth: false,
            },
        },
        {
            path: "code-preview",
            name: "code-preview",
            component: CodePreviewDemo,
            meta: {
                requiresAuth: false,
                layout: "DevLayout",
            },
        },
        {
            path: "spinner",
            name: "spinner-demo",
            component: SpinnerDemo,
            meta: {
                requiresAuth: false,
                layout: "DevLayout",
            },
        },
        {
            path: "task-evaluation",
            component: TaskEvaluation,
            meta: {
                requiresAuth: true,
                layout: "DevLayout",
            },
            children: [
                {
                    path: "",
                    name: "task-evaluation",
                    component: () => import("@/views/TaskEvaluationHome.vue"),
                },
                // Generic route pattern for all task types
                {
                    path: ":taskType",
                    name: "task-evaluation-list",
                    component: () => import("@/views/TaskEvaluationList.vue"),
                    props: (route) => {
                        const taskTypeMap = {
                            "generate-description": {
                                apiType: "generate_description",
                                name: "Generate Description",
                            },
                            "generate-definition-xml": {
                                apiType: "generate_definition_xml",
                                name: "Generate BPMN Definition XML",
                            },
                            "generate-ui": {
                                apiType: "generate_user_interface",
                                name: "Generate UI Component",
                            },
                        };
                        const taskInfo =
                            taskTypeMap[route.params.taskType as string];
                        return {
                            taskType: taskInfo.apiType,
                            taskName: taskInfo.name,
                        };
                    },
                },
                {
                    path: ":taskType/configure",
                    name: "task-evaluation-configure",
                    component: () =>
                        import("@/components/TaskEvaluation/GenericTaskConfiguration.vue"),
                    props: (route) => ({
                        taskType: route.params.taskType as string,
                    }),
                },
                {
                    path: ":taskType/:id",
                    name: "task-evaluation-detail",
                    component: () => import("@/views/TaskEvaluationList.vue"),
                    props: (route) => {
                        const taskTypeMap = {
                            "generate-description": {
                                apiType: "generate_description",
                                name: "Generate Description",
                            },
                            "generate-definition-xml": {
                                apiType: "generate_definition_xml",
                                name: "Generate BPMN Definition XML",
                            },
                            "generate-ui": {
                                apiType: "generate_user_interface",
                                name: "Generate UI Component",
                            },
                        };
                        const taskInfo =
                            taskTypeMap[route.params.taskType as string];
                        return {
                            taskType: taskInfo.apiType,
                            taskName: taskInfo.name,
                            evaluationId: route.params.id,
                        };
                    },
                },
            ],
        },
    ],
};

const routes = [...baseRoutes];
if (import.meta.env.MODE !== "production") {
    routes.push(devRoute);
}

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        window.scrollTo(0, 0);
    },
});

export let routerIsReady = false;
// Make sure we can do something right after the router is ready.
router.isReady().then(async () => {
    const { authStatus } = useAuthenticationManager();
    const { isInitialized } = useAuthenticationStore();
    routerIsReady = true;

    const requiresAuth = router.currentRoute.value.meta.requiresAuth;
    const currentRouteName = String(router.currentRoute.value.name);

    // Sometimes the authentication library is initialized before the router is,
    // in those cases we still need to redirect to the right pages.
    await isInitialized;
    switch (authStatus.value) {
        case AuthStatus.SIGNED_IN: {
            if (
                ["login", "signup", "forgotPassword"].includes(currentRouteName)
            ) {
                router.push("/");
            }
            break;
        }
        case AuthStatus.MFA_REQUIRED:
        case AuthStatus.MFA_ENROLLMENT_REQUIRED:
        case AuthStatus.EMAIL_VERIFICATION_REQUIRED: {
            if (currentRouteName !== "login") {
                router.push({
                    name: "login",
                    query: {
                        returnTo: encodeURIComponent(
                            JSON.stringify({
                                name: router.currentRoute.value.name,
                                params: router.currentRoute.value.params,
                                query: router.currentRoute.value.query,
                            })
                        ),
                    },
                });
            }
            break;
        }
        case AuthStatus.NOT_SIGNED_IN: {
            // Special case for root path: redirect to /home instead of /login
            if (router.currentRoute.value.fullPath === "/") {
                router.push("/home");
            } else if (requiresAuth) {
                router.push({
                    name: "login",
                    query: {
                        returnTo: encodeURIComponent(
                            JSON.stringify({
                                name: router.currentRoute.value.name,
                                params: router.currentRoute.value.params,
                                query: router.currentRoute.value.query,
                            })
                        ),
                    },
                });
            }
            break;
        }
    }
});

const AUTH_RECORDING_ROUTES = ["login", "signup", "forgotPassword"];

// First guard — flush all pending throttled writes before navigation so that
// the sync-store's router.beforeEach can pick up the queued entries.
router.beforeEach(() => {
    flushAll();
});

router.beforeEach(async (guard) => {
    // Start recording when entering auth flows so login and MFA setup are captured.
    // Stop recording when leaving auth routes so sessions don't extend into the app.
    if (AUTH_RECORDING_ROUTES.includes(String(guard.name))) {
        mixpanel.start_session_recording();
    } else {
        mixpanel.stop_session_recording();
    }

    mixpanel.track_pageview({
        "Page Name": guard.name,
        "Page URL": guard.path,
        ...guard.params,
    });

    const { isInitialized } = useAuthenticationStore();
    // There is a brief period in which the authentication library is still
    // initializing. In this period the authentication status is unknown.
    // As soon as the authentication status is known it will be updated in
    // the store, so we utilize a short-lived watcher so that we can wait
    // for the authentication status to be updated in the router guard.
    await isInitialized;

    // Special case for root path when not authenticated
    const { authStatus } = useAuthenticationManager();
    if (
        guard.fullPath === "/" &&
        authStatus.value === AuthStatus.NOT_SIGNED_IN
    ) {
        return { path: "/home" };
    }
});

export default router;
